import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import useAuth from '../hooks/useAuth';
import { ApiClientError, api } from '../services/api';
import type { VoiceProfile, VoiceSettings } from '../types';

export const VOICE_STATUS_LABELS: Record<VoiceProfile['status'], string> = {
  pending_enrollment: 'In afwachting',
  processing: 'Verwerken',
  active: 'Actief',
  failed: 'Mislukt',
  deleted: 'Verwijderd',
};

export function voiceProviderLabel(provider: string): string {
  switch (provider) {
    case 'mock':
      return 'Mock (ontwikkel/test)';
    case 'elevenlabs':
      return 'ElevenLabs';
    case 'cartesia':
      return 'Cartesia';
    case 'resemble':
      return 'Resemble AI';
    default:
      return provider;
  }
}

export function voiceProviderIsProduction(provider?: string): boolean {
  if (!provider) return false;
  return provider !== 'mock';
}

export function formatVoiceDate(iso?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export type VoiceStatus = 'idle' | 'loading' | 'ready' | 'error';

export type VoiceErrorType =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'server'
  | 'network'
  | 'unknown';

export type VoiceError = {
  type: VoiceErrorType;
  message: string;
  canRetry: boolean;
};

export type VoiceContextValue = {
  status: VoiceStatus;
  profile: VoiceProfile | null;
  settings: VoiceSettings | null;
  error: VoiceError | null;
  isLoading: boolean;
  isReady: boolean;
  hasActiveTwin: boolean;
  isMock: boolean;
  refresh: () => void;
};

export const VoiceContext = createContext<VoiceContextValue | null>(null);

const classifyError = (reason: unknown): VoiceError => {
  if (reason instanceof ApiClientError) {
    if (reason.status === 401) {
      return {
        type: 'unauthorized',
        message: 'Je sessie is verlopen. Log opnieuw in om Voice Twin te bekijken.',
        canRetry: false,
      };
    }
    if (reason.status === 403) {
      return {
        type: 'forbidden',
        message: 'Je account heeft geen toegang tot Voice Twin.',
        canRetry: false,
      };
    }
    if (reason.status === 404) {
      return {
        type: 'not_found',
        message: 'Voice Twin-status kon niet worden gevonden.',
        canRetry: true,
      };
    }
    if (reason.status >= 500) {
      return {
        type: 'server',
        message: 'De server kon de Voice-status niet laden. Probeer het later opnieuw.',
        canRetry: true,
      };
    }
    return {
      type: 'unknown',
      message: 'Er is een onverwachte fout opgetreden bij het laden.',
      canRetry: true,
    };
  }
  if (reason instanceof Error && reason.name === 'AbortError') {
    return {
      type: 'unknown',
      message: 'Het verzoek werd afgebroken.',
      canRetry: true,
    };
  }
  return {
    type: 'network',
    message: 'Verbinding mislukt. Controleer je internetverbinding en probeer opnieuw.',
    canRetry: true,
  };
};

export const VoiceProvider = ({ children }: { children: ReactNode }) => {
  const { state: authState } = useAuth();
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [profile, setProfile] = useState<VoiceProfile | null>(null);
  const [settings, setSettings] = useState<VoiceSettings | null>(null);
  const [error, setError] = useState<VoiceError | null>(null);

  const fetchVoiceStatus = useCallback(async (signal?: AbortSignal) => {
    if (authState !== 'authenticated') return null;
    const [profile, settings] = await Promise.all([
      api.voiceProfile(signal),
      api.voiceSettings(signal),
    ]);
    return { profile, settings };
  }, [authState]);

  const applyVoiceResult = useCallback((result: { profile: VoiceProfile | null; settings: VoiceSettings } | null) => {
    if (result) {
      setProfile(result.profile);
      setSettings(result.settings);
      setStatus('ready');
      setError(null);
    } else {
      setProfile(null);
      setSettings(null);
      setStatus('idle');
      setError(null);
    }
  }, []);

  const applyVoiceError = useCallback((reason: unknown) => {
    const e = classifyError(reason);
    setError(e);
    setStatus('error');
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  // Voice status is loaded as an external data source once auth is known.
  useEffect(() => {
    if (authState !== 'authenticated') {
      setStatus('idle');
      setProfile(null);
      setSettings(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    setError(null);
    void fetchVoiceStatus(controller.signal)
      .then(applyVoiceResult)
      .catch(applyVoiceError);
    return () => controller.abort();
  }, [authState, fetchVoiceStatus, applyVoiceResult, applyVoiceError]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const refresh = useCallback(() => {
    const controller = new AbortController();
    setStatus('loading');
    setError(null);
    void fetchVoiceStatus(controller.signal)
      .then(applyVoiceResult)
      .catch(applyVoiceError);
  }, [fetchVoiceStatus, applyVoiceResult, applyVoiceError]);

  const value = useMemo<VoiceContextValue>(
    () => ({
      status,
      profile,
      settings,
      error,
      isLoading: status === 'loading',
      isReady: status === 'ready',
      hasActiveTwin: profile?.status === 'active',
      isMock: profile?.provider === 'mock',
      refresh,
    }),
    [status, profile, settings, error, refresh],
  );

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};
